import './polyfills';

import './app/theming/fonts/material-icons.css';
import './app/theming/fonts/inter.css';
import { LicenseInfo } from '@mui/x-license';
import is from '@sindresorhus/is';
import { createConsola } from 'consola/browser';
import i18next from 'i18next';
import Maybe from 'true-myth/maybe';

import { App } from './app';
import { createCustomizationProvider } from './customization';
import { initDataDog, setDatadogTenantId } from './data-dog';
import { PrimaryActionButton } from './design/components/button/PrimaryAction';
import { ErrorPage } from './design/components/errors/ErrorPage';
import { createInternationalization } from './internationalization/internationalization';
import { createConsoleLogger } from './logger/console-logger';
import { render } from './render-app';
import { setFavicon } from './set-favicon';
import { createAuthorizationProvider } from '../../shared/source/authorization';
import { createRealClock } from '../../shared/source/clock/real-clock';
import { appEnvironmentSchema } from '../../shared/source/environment/environment';
import { createFeatureFlagService } from '../../shared/source/feature-flag-service';
import { createHttpFetcher } from '../../shared/source/http-fetcher';
import { createQueryClient } from '../../shared/source/query-client/query-client';
import { createRealtimeEventListenerBuilder } from '../../shared/source/realtime-event-listener';
import { createValidatingHttpFetcher } from '../../shared/source/validating-http-fetcher/validating-http-fetcher';

import type { Logger } from '../../shared/source/logger/logger';
import type { ErrorReporter } from '../../shared/source/validating-http-fetcher/validating-http-fetcher';

const setupErrorReporter = (datadogEnabled: boolean, logger: Logger): ErrorReporter => {
    if (datadogEnabled) {
        return initDataDog();
    }

    logger.info('DataDog logging disabled due to environment settings');

    return {
        reportError: (message, error) => logger.error(message, error),
    };
};

export const startApp = async (): Promise<void> => {
    const { t: translate } = i18next;

    try {
        const environment = appEnvironmentSchema.parse(import.meta.env);
        const apiBaseUrl = new URL(environment.apiBaseUrl);
        const apiRealtimeBaseUrl = new URL(environment.apiRealtimeBaseUrl);
        const { enableDataDog, launchDarklyBaseUrl, launchDarklyEventsUrl, muiXLicenseKey, refetchOnFocus } =
            environment;
        const logger = createConsoleLogger({ createConsoleTarget: createConsola }, { logLevel: 'silent' });

        const datadogEnabled = import.meta.env.PROD === true || enableDataDog;
        const errorReporter = setupErrorReporter(datadogEnabled, logger);
        const cobrainerApiFetcher = createHttpFetcher(
            {
                errorReporter,
            },
            {
                baseUrl: apiBaseUrl,
                requestHeaders: {
                    get CorrelationId() {
                        return globalThis.crypto.randomUUID();
                    },
                    get RequestId() {
                        return globalThis.crypto.randomUUID();
                    },
                },
            },
        );

        const validatingCobrainerApiFetcher = createValidatingHttpFetcher({
            errorReporter,
            httpFetcher: cobrainerApiFetcher,
        });

        const realtimeEventListenerBuilder = createRealtimeEventListenerBuilder({
            baseUrl: apiRealtimeBaseUrl,
        });

        const realtimeApiFetcher = createHttpFetcher(
            {
                errorReporter,
            },
            {
                baseUrl: apiRealtimeBaseUrl,
            },
        );

        const validatingRealtimeApiFetcher = createValidatingHttpFetcher({
            errorReporter,
            httpFetcher: realtimeApiFetcher,
        });

        const customizationProvider = await createCustomizationProvider(
            { validatingCobrainerApiFetcher },
            {
                location: globalThis.location,
            },
        );

        const {
            id: _customizationId,
            oidcConfiguration,
            tenantId,
            translationOverrides,
        } = customizationProvider.customization.unwrapOrElse(() => {
            throw new Error('No customization found');
        });

        if (datadogEnabled) {
            setDatadogTenantId(tenantId);
        }

        await createInternationalization({ i18nProvider: i18next }, { translationOverrides });

        const authorizationProvider = await createAuthorizationProvider(
            {
                apiBaseUrl,
                clock: createRealClock(),
                logger,
                validatingHttpFetcher: createValidatingHttpFetcher({
                    errorReporter,
                    httpFetcher: createHttpFetcher({
                        errorReporter,
                    }),
                }),
            },
            {
                oidcConfiguration,
                onAuthorizationReceived: (bearerToken: string) => {
                    const { assumedTenantId, isDefaultCustomization } = customizationProvider;
                    const customizationId = !isDefaultCustomization
                        ? Maybe.just<string>(_customizationId)
                        : Maybe.nothing<string>();

                    validatingCobrainerApiFetcher.requestHeaders = {
                        Authorization: `Bearer ${bearerToken}`,
                        ...(customizationId.isJust ? { 'Customization-Id': customizationId.value } : {}),
                        ...(assumedTenantId.isJust ? { 'Tenant-Id': assumedTenantId.value } : {}),
                    };

                    validatingCobrainerApiFetcher.addHttpErrorHandler((error) => {
                        if (error.response.status === 401) {
                            authorizationProvider.logout();
                        }
                    });

                    realtimeEventListenerBuilder.requestHeaders = {
                        Authorization: `Bearer ${bearerToken}`,
                    };

                    realtimeApiFetcher.requestHeaders = {
                        Authorization: `Bearer ${bearerToken}`,
                    };
                },
            },
        );

        const featureFlagService = createFeatureFlagService({ launchDarklyBaseUrl, launchDarklyEventsUrl, tenantId });

        const queryClient = createQueryClient(refetchOnFocus, authorizationProvider.logout);

        // Set history state for initialLoad for BackNavigation
        globalThis.history.replaceState({ initialLoad: true }, globalThis.document.title);

        if (is.string(muiXLicenseKey)) {
            LicenseInfo.setLicenseKey(muiXLicenseKey);
        }

        setFavicon(customizationProvider.customization);

        render(
            <App
                dependencies={{
                    authorizationProvider,
                    clock: createRealClock(),
                    cobrainerApiFetcher,
                    customizationProvider,
                    featureFlagService,
                    initializeFeatureFlagService: ({ userId }) => featureFlagService.initialize(userId),
                    logger,
                    realtimeApiFetcher,
                    realtimeEventListenerBuilder,
                    translate,
                    validatingCobrainerApiFetcher,
                    validatingRealtimeApiFetcher,
                }}
                environment={environment}
                queryClient={queryClient}
            />,
        );
    } catch (error: unknown) {
        render(
            <ErrorPage message={translate('errors.fatal.message')} title={translate('errors.fatal.title')}>
                <PrimaryActionButton
                    label={translate('common.errors.action')}
                    onClick={() => globalThis.location.reload()}
                />
            </ErrorPage>,
        );
    }
};

void startApp();
